import type { ReactNode } from "react"
import { Link } from "react-router-dom"
import { Badge } from "@/components/ui/badge"
import { buttonVariants } from "@/components/ui/button"
import { BOXED_ROW_CLASS, ROW_BREAK_CLASS, ROW_LAYOUT_CLASS } from "@/components/dashboard/DashboardSection"
import { collaborationStatusConfig, type CollaborationStatusKey } from "@/lib/section-status"
import { cn } from "@/lib/utils"
import type { MyWorkItem } from "@/stores/my-work-store"
import { formatWordCount, nextStepLabel, sectionEditorPath, type FromMyWorkState } from "@/lib/my-work-row"

// One row for a section assigned to (or awaiting review by) the current user.
// Home's "Up next" and the My Work page both render this, so the same item
// reads the same way on both.

export function WorkStatusPill({ status }: { status: MyWorkItem['collaborationStatus'] }) {
  const cfg = collaborationStatusConfig[status as CollaborationStatusKey] ?? collaborationStatusConfig.not_started
  return (
    <Badge variant={cfg.variant} dot className="whitespace-nowrap font-normal" data-testid="work-status-pill">
      {cfg.label}
    </Badge>
  )
}

interface WorkItemRowProps {
  item: MyWorkItem
  /** Lead line 2 with the bid name — for lists that aren't grouped by bid (Home). */
  showBid?: boolean
  /** Extra content between the text and the next-step button (Home's due chip). */
  aside?: ReactNode
  /** Router state for the editor link. */
  linkState?: FromMyWorkState
  testId?: string
}

export function WorkItemRow({ item, showBid = false, aside, linkState, testId = "work-item-row" }: WorkItemRowProps) {
  const words = formatWordCount(item.wordCount)
  const reviewing =
    item.role === 'writer' && item.collaborationStatus === 'in_review' && item.reviewerName
      ? `${item.reviewerName} reviewing`
      : null
  const meta = [showBid ? item.bidTitle : null, item.artifactName, words, reviewing].filter(Boolean).join(' · ')

  return (
    <Link
      to={sectionEditorPath(item)}
      state={linkState}
      className={cn(ROW_LAYOUT_CLASS, BOXED_ROW_CLASS)}
      data-testid={testId}
    >
      <div className="shrink-0 sm:w-[8.75rem]">
        <WorkStatusPill status={item.collaborationStatus} />
      </div>
      {/* Phones: label and aside on line one, title and action on line two. */}
      <span aria-hidden="true" className={cn(ROW_BREAK_CLASS, "max-sm:order-1")} />
      <div className="min-w-0 flex-1 max-sm:order-2">
        <p className="truncate text-[13px] font-medium">{item.sectionTitle}</p>
        <p className="truncate text-meta mt-0.5">{meta}</p>
      </div>
      {aside}
      {/* A label for the row's own action, not a second control — the whole row is the link. */}
      <span
        className={cn(
          buttonVariants({ variant: "outline", size: "sm" }),
          "pointer-events-none min-w-[5.5rem] shrink-0 group-hover:bg-muted max-sm:order-3",
        )}
        data-testid="next-step"
      >
        {nextStepLabel(item)}
      </span>
    </Link>
  )
}
